import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { Email, Folder } from '@/types/email';
import type { SenderGroupInfo } from '@/features/email-list/types';
import { useSenderGroupingStore } from './senderGroupingStore';
import { useFolderStore } from './folderStore';

type DraggedEmailEntry = {
  emailId: string;
  accountId: string;
  folderId: string;
};

type DragState =
  | {
      type: 'single';
      items: DraggedEmailEntry[];
      primaryEmailId: string;
      totalCount: number;
    }
  | {
      type: 'group';
      items: DraggedEmailEntry[];
      primaryEmailId: string;
      groupKey: string;
      totalCount: number;
    };

interface DragDropStoreState {
  draggedItem: DragState | null;
  dragHydrationRequestId: number;
  activeDropFolderKey: string | null;
  startEmailDrag: (email: Email) => void;
  startSenderGroupDrag: (group: SenderGroupInfo) => void;
  endDrag: () => void;
  handleFolderDragEnter: (folder: Folder) => void;
  handleFolderDragLeave: (folder: Folder) => void;
  canDropOnFolder: (folder: Folder) => boolean;
  dropOnFolder: (folder: Folder) => Promise<void>;
}

const getFolderSourceCandidates = (folder: Folder) => {
  if (folder.sourceFolders && folder.sourceFolders.length > 0) {
    return folder.sourceFolders;
  }
  if (folder.accountId) {
    return [{ accountId: folder.accountId, folderId: folder.id }];
  }
  return [];
};

const getFolderKey = (folder: Folder) => {
  const sources = getFolderSourceCandidates(folder);
  if (sources.length === 1) {
    const [source] = sources;
    return `${source.accountId}:${source.folderId}`;
  }
  if (sources.length > 1) {
    return folder.id;
  }
  if (folder.accountId) {
    return `${folder.accountId}:${folder.id}`;
  }
  return folder.id;
};

export const useDragDropStore = create<DragDropStoreState>()(
  conditionalDevtools((set, get) => ({
    draggedItem: null,
    dragHydrationRequestId: 0,
    activeDropFolderKey: null,

    startEmailDrag: (email) => {
      const folderId = email.folderId ?? useFolderStore.getState().selectedFolderId() ?? null;
      if (!folderId) {
        return;
      }
      set((state) => ({
        draggedItem: {
          type: 'single',
          items: [{ emailId: email.id, accountId: email.accountId, folderId }],
          primaryEmailId: email.id,
          totalCount: 1,
        },
        dragHydrationRequestId: state.dragHydrationRequestId + 1,
        activeDropFolderKey: null,
      }));
    },

    startSenderGroupDrag: (group) => {
      const fallbackFolderId = useFolderStore.getState().selectedFolderId();
      const initialEntries = new Map<string, DraggedEmailEntry>();
      group.emails.forEach((email) => {
        const folderId = email.folderId ?? fallbackFolderId ?? null;
        if (email.id && email.accountId && folderId) {
          initialEntries.set(email.id, {
            emailId: email.id,
            accountId: email.accountId,
            folderId,
          });
        }
      });

      if (!initialEntries.size) {
        return;
      }

      const totalCount = group.totalMessages ?? initialEntries.size;
      const requestId = get().dragHydrationRequestId + 1;
      set({
        draggedItem: {
          type: 'group',
          items: Array.from(initialEntries.values()),
          primaryEmailId: initialEntries.values().next().value?.emailId ?? group.key,
          groupKey: group.key,
          totalCount,
        },
        activeDropFolderKey: null,
        dragHydrationRequestId: requestId,
      });

      if (totalCount <= initialEntries.size) {
        return;
      }

      void (async () => {
        try {
          const emails = await useSenderGroupingStore
            .getState()
            .collectSenderEmailsForGroup(group, { limit: totalCount });

          if (get().dragHydrationRequestId !== requestId) {
            return;
          }

          const combined = new Map<string, DraggedEmailEntry>();
          emails.forEach((email) => {
            if (email.id && email.accountId && email.folderId) {
              combined.set(email.id, {
                emailId: email.id,
                accountId: email.accountId,
                folderId: email.folderId,
              });
            }
          });

          set((state) => {
            if (
              !state.draggedItem ||
              state.draggedItem.type !== 'group' ||
              state.draggedItem.groupKey !== group.key
            ) {
              return {};
            }
            const mergedItems = Array.from(combined.values());
            return {
              draggedItem: {
                ...state.draggedItem,
                items: mergedItems,
                totalCount: mergedItems.length,
                primaryEmailId:
                  state.draggedItem.primaryEmailId && combined.has(state.draggedItem.primaryEmailId)
                    ? state.draggedItem.primaryEmailId
                    : mergedItems[0]?.emailId ?? state.draggedItem.primaryEmailId,
              },
            };
          });
        } catch (error) {
          console.error('Failed to hydrate sender group drag data', error);
        }
      })();
    },

    endDrag: () => {
      set((state) => ({
        draggedItem: null,
        activeDropFolderKey: null,
        dragHydrationRequestId: state.dragHydrationRequestId + 1,
      }));
    },

    handleFolderDragEnter: (folder) => {
      if (!get().draggedItem) {
        return;
      }
      if (!get().canDropOnFolder(folder)) {
        return;
      }
      const folderKey = getFolderKey(folder);
      // Only update if the folder actually changed to avoid unnecessary re-renders
      if (get().activeDropFolderKey !== folderKey) {
        set({ activeDropFolderKey: folderKey });
      }
    },

    handleFolderDragLeave: (folder) => {
      const key = getFolderKey(folder);
      if (get().activeDropFolderKey === key) {
        set({ activeDropFolderKey: null });
      }
    },

    canDropOnFolder: (folder) => {
      const dragged = get().draggedItem;
      if (!dragged) {
        return false;
      }
      return getMovableItemsForFolder(dragged, folder).length > 0;
    },

    dropOnFolder: async (folder) => {
      const dragged = get().draggedItem;
      if (!dragged) {
        return;
      }
      const moves = getMovableItemsForFolder(dragged, folder);
      if (!moves.length) {
        get().endDrag();
        return;
      }

      const uniqueEmailIds = Array.from(new Set(moves.map((move) => move.emailId)));
      // Per-email destinations are already resolved against the drop target's
      // source folders — hand them to the engine, which owns optimistic
      // removal, per-email undo, partial-failure restore, and toasts.
      const destinationById = new Map<string, string>(
        moves.map((move) => [move.emailId, move.targetFolderId])
      );

      try {
        const { executeEmailAction } = await import('./emailCommands');
        await executeEmailAction(
          'move',
          { kind: 'messages', ids: uniqueEmailIds },
          { destination: { type: 'perEmail', byId: destinationById, name: folder.name } }
        );
      } finally {
        get().endDrag();
      }
    },
  }))
);

interface MoveCandidate {
  emailId: string;
  accountId: string;
  sourceFolderId: string;
  targetFolderId: string;
}

const getMovableItemsForFolder = (dragged: DragState, folder: Folder): MoveCandidate[] => {
  const sources = getFolderSourceCandidates(folder);
  if (!sources.length) {
    return [];
  }
  const candidates = new Map<string, MoveCandidate>();
  dragged.items.forEach((entry) => {
    if (!entry.accountId || !entry.folderId) {
      return;
    }
    const match = sources.find(
      (source) => source.accountId === entry.accountId && source.folderId !== entry.folderId
    );
    if (!match) {
      return;
    }
    if (!candidates.has(entry.emailId)) {
      candidates.set(entry.emailId, {
        emailId: entry.emailId,
        accountId: entry.accountId,
        sourceFolderId: entry.folderId,
        targetFolderId: match.folderId,
      });
    }
  });
  return Array.from(candidates.values());
};
